import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Mail,
  Send,
  CheckCircle,
  Shield,
  Rocket
} from 'lucide-react';

const Login = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { sendMagicLink, verifyMagicToken, currentUser, loading } = useAuth();

  const [email, setEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [sentDetails, setSentDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [verifyingToken, setVerifyingToken] = useState(false);

  // Check if token exists in URL search params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (token) {
      setVerifyingToken(true);
      verifyMagicToken(token)
        .then(() => {
          setVerifyingToken(false);
          const destination = location.state?.from?.pathname || '/dashboard';
          navigate(destination);
        })
        .catch((err) => {
          setVerifyingToken(false);
          setErrorMessage(err.message || 'Verification failed. Please request a new magic link.');
        });
    }
  }, [location.search, verifyMagicToken, navigate, location.state]);

  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    try {
      const result = await sendMagicLink(email);
      setMagicLinkSent(true);
      setSentDetails(result);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to send magic link. Please try again.');
    }
  };

  if (verifyingToken) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
        <h2 className="text-2xl font-bold mb-2">Verifying Magic Link...</h2>
        <p className={isDarkMode ? 'text-dark-300' : 'text-light-600'}>
          Signing you in to SkillMesh...
        </p>
      </div>
    );
  }

  return (
    <div className={`max-w-xl mx-auto py-8 px-4 transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Header Banner */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-primary-500/10 text-primary-400 text-sm font-medium mb-4">
          <Rocket className="h-4 w-4" />
          <span>Skills & Team Network</span>
        </div>
        <h1 className="text-4xl font-extrabold gradient-text mb-3">
          Join SkillMesh
        </h1>
        <p className={`text-base transition-colors duration-200 ${
          isDarkMode ? 'text-dark-300' : 'text-light-600'
        }`}>
          Log in with an email magic link to map your skills, build projects, and connect with teammates.
        </p>
      </div>

      {/* Notice if redirected from a protected route */}
      {location.state?.from && !currentUser && (
        <div className={`mb-6 p-4 rounded-xl border text-sm flex items-center space-x-3 ${
          isDarkMode 
            ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' 
            : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}>
          <Shield className="h-5 w-5 flex-shrink-0 text-amber-400" />
          <div>
            <span className="font-semibold">Sign in required: </span>
            <span>You must be signed in to access <code className="px-1.5 py-0.5 rounded bg-black/20 font-mono text-xs">{location.state.from.pathname}</code> and view team data.</span>
          </div>
        </div>
      )}

      {/* Main Login Card */}
      <div className={`card transition-all duration-200 mb-8 ${
        isDarkMode ? 'dark' : 'light'
      }`}>
        {magicLinkSent ? (
          <div className="text-center py-6 space-y-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-2 bg-green-500/20 text-green-400">
              <CheckCircle className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-bold">
              Check Your Email!
            </h2>
            <p className={isDarkMode ? 'text-dark-300' : 'text-light-600'}>
              We sent a secure sign-in link to <strong className="text-primary-400">{sentDetails?.email || email}</strong>.
            </p>

            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 text-sm text-green-400 text-left space-y-1">
              <div>✉️ <strong>Open your email inbox</strong> and click the direct sign-in button.</div>
              <div className="text-xs text-green-500/80">The magic link is valid for 15 minutes and can only be used once. Check your spam folder if you do not see it within a minute.</div>
            </div>

            <button
              onClick={() => {
                setMagicLinkSent(false);
                setSentDetails(null);
              }}
              className={`text-sm underline mt-4 ${
                isDarkMode ? 'text-dark-300 hover:text-white' : 'text-light-600 hover:text-light-900'
              }`}
            >
              ← Use a different email address
            </button>
          </div>
        ) : (
          <form onSubmit={handleSendMagicLink} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                isDarkMode ? 'text-white' : 'text-light-800'
              }`}>
                Email Address
              </label>
              <div className="relative">
                <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${
                  isDarkMode ? 'text-dark-400' : 'text-light-500'
                }`} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com or builder@skillmesh.io"
                  className={`input-field w-full pl-10 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                  required
                />
              </div>
            </div>

            {errorMessage && (
              <div className="p-3 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center space-x-2 py-3"
            >
              {loading ? (
                <span>Sending Magic Link...</span>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  <span>Send Email Magic Link</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>

      {/* Current logged in status */}
      {currentUser && (
        <div className="mt-6 text-center text-sm">
          <span className={isDarkMode ? 'text-dark-300' : 'text-light-600'}>Currently logged in as </span>
          <strong className="text-primary-400">{currentUser.name}</strong> ({currentUser.role === 'admin' ? 'Admin' : 'Participant'})
          <button
            onClick={() => navigate('/dashboard')}
            className="ml-2 text-primary-400 underline font-medium"
          >
            Go to Dashboard →
          </button>
        </div>
      )}
    </div>
  );
};

export default Login;
